(function () {
  const sanitizeFont = (font) => String(font || "Basteleur Moonlight").replace(/[;"{}]/g, "");

  const fontStack = (font) => {
    const label = String(font || "").trim();
    const safe = sanitizeFont(label === "Signature" ? "Basteleur Moonlight" : label === "Typewriter" ? "Courier Prime" : label === "Heritage" ? "Telma" : label);
    if (safe === "Courier Prime") return '"Courier Prime", "Courier New", Courier, monospace';
    return `"${safe}", Georgia, serif`;
  };

  const engravingTemplate = Object.freeze({
    centerX: 0.5,
    centerY: 0.505,
    stoneRadiusX: 0.205,
    stoneRadiusY: 0.265,
    safeInset: 0.18
  });

  const engravingEllipse = (width, height) => ({
    cx: width * engravingTemplate.centerX,
    cy: height * engravingTemplate.centerY,
    rx: width * engravingTemplate.stoneRadiusX * (1 - engravingTemplate.safeInset),
    ry: height * engravingTemplate.stoneRadiusY * (1 - engravingTemplate.safeInset)
  });

  const numberFrom = (value, fallback) => {
    const parsed = Number.parseFloat(String(value || "").replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const colorFrom = (value) => {
    const match = String(value || "").match(/#[0-9a-f]{6}\b/i);
    return match ? match[0] : "#F7F6F2";
  };

  const authoritativeFontAssets = Object.freeze({
    "basteleur-moonlight-v1": Object.freeze({family: "Basteleur Moonlight", cssFamily: "Kova Basteleur Moonlight v1"}),
    "courier-prime-regular-v1": Object.freeze({family: "Courier Prime", cssFamily: "Kova Courier Prime Regular v1"}),
    "velvelyne-regular-v1": Object.freeze({family: "Velvelyne", cssFamily: "Kova Velvelyne Regular v1"}),
    "la-belle-aurore-regular-v1": Object.freeze({family: "La Belle Aurore", cssFamily: "Kova La Belle Aurore Regular v1"}),
    "telma-regular-v1": Object.freeze({family: "Telma", cssFamily: "Kova Telma Regular v1"})
  });
  // Keep this customer-facing palette in sync with the app's production color catalog.
  const authoritativeColors = Object.freeze(["#C0C0C0", "#D4AF37", "#F7F6F2", "#E6DF9A", "#C9A34E", "#E8D5A3", "#43523B", "#1E1C19", "#F9F06F"]);
  const millimeterFloatTolerance = Number.EPSILON * 35;

  const wrapTextToEllipse = (ctx, value, fontSize, stack, maxWidth, maxLines = 4) => {
    const text = String(value || "").trim();
    if (!text) return [];

    ctx.font = `400 ${fontSize}px ${stack}`;
    const lines = [];
    const pushLine = (line) => {
      if (lines.length >= maxLines) return false;
      lines.push(line);
      return true;
    };

    const pushLongToken = (token) => {
      let segment = "";
      for (const character of token) {
        const candidate = segment + character;
        if (segment && ctx.measureText(candidate).width > maxWidth) {
          if (!pushLine(segment)) return "";
          segment = character;
        } else {
          segment = candidate;
        }
      }
      return segment;
    };

    for (const paragraph of text.split(/\r?\n/)) {
      const words = paragraph.trim().split(/\s+/).filter(Boolean);
      if (!words.length) {
        if (!pushLine("")) break;
        continue;
      }

      let current = "";
      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxWidth) {
          current = candidate;
          continue;
        }
        if (current && !pushLine(current)) return lines;
        current = ctx.measureText(word).width <= maxWidth ? word : pushLongToken(word);
        if (!current && lines.length >= maxLines) return lines;
      }
      if (current && !pushLine(current)) break;
    }

    return lines;
  };

  const drawStone = (ctx, canvas, image) => {
    const width = canvas.width;
    const height = canvas.height;
    const ratio = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const imageWidth = image.naturalWidth * ratio;
    const imageHeight = image.naturalHeight * ratio;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#f4f1ea";
    ctx.fillRect(0, 0, width, height);
    ctx.save();
    ctx.shadowColor = "rgba(36, 48, 38, 0.1)";
    ctx.shadowBlur = 22;
    ctx.shadowOffsetY = 10;
    ctx.drawImage(image, (width - imageWidth) / 2, (height - imageHeight) / 2, imageWidth, imageHeight);
    ctx.restore();
  };

  const drawReadablePreview = (canvas, data, image) => {
    const ctx = canvas.getContext("2d");
    if (!ctx || !image.naturalWidth || !image.naturalHeight) return false;

    const width = canvas.width;
    const height = canvas.height;
    const fontSize = Math.max(20, Math.round(width * (numberFrom(data.size, 9) / 100)));
    const stack = fontStack(data.font);
    const ellipse = engravingEllipse(width, height);
    const lines = wrapTextToEllipse(ctx, data.text, fontSize, stack, ellipse.rx * 1.72, 4);
    if (!lines.length) return false;

    drawStone(ctx, canvas, image);

    ctx.save();
    ctx.beginPath();
    ctx.ellipse(ellipse.cx, ellipse.cy, ellipse.rx, ellipse.ry, 0, 0, Math.PI * 2);
    ctx.clip();
    ctx.translate(width * (numberFrom(data.x, 50) / 100), height * (numberFrom(data.y, 52) / 100));
    ctx.rotate((numberFrom(data.rotation, 0) * Math.PI) / 180);
    ctx.font = `400 ${fontSize}px ${stack}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0, 0, 0, 0.24)";
    ctx.shadowBlur = 7;
    ctx.shadowOffsetY = 2;

    const lineHeight = fontSize * 1.14;
    lines.forEach((line, index) => {
      const offset = (index - (lines.length - 1) / 2) * lineHeight;
      ctx.strokeStyle = "rgba(20, 24, 20, 0.2)";
      ctx.lineWidth = Math.max(3, fontSize * 0.08);
      ctx.strokeText(line, 0, offset);
      ctx.fillStyle = colorFrom(data.color);
      ctx.fillText(line, 0, offset);
    });
    ctx.restore();
    return true;
  };

  const normalizedEngravingFrom = (previewDesign) => {
    const engraving = previewDesign && previewDesign.engraving;
    const lines = engraving && engraving.normalizedLines;
    if (!engraving || !Array.isArray(lines) || lines.length < 1 || lines.length > 4) {
      throw new Error("The normalized engraving is unavailable.");
    }
    if (!lines.every((line) => typeof line === "string" && !/[\0\r\n]/.test(line))) {
      throw new Error("The normalized engraving lines are invalid.");
    }
    const normalizedText = lines.join("\n");
    if (!normalizedText.trim() || Array.from(normalizedText).length > 24) {
      throw new Error("The normalized engraving text is invalid.");
    }
    const fontAsset = typeof engraving.fontAssetId === "string" &&
      authoritativeFontAssets[engraving.fontAssetId];
    if (!fontAsset || fontAsset.family !== engraving.fontFamily) {
      throw new Error("The normalized engraving font asset is invalid.");
    }
    if (!authoritativeColors.includes(engraving.colorHex)) {
      throw new Error("The normalized engraving color is invalid.");
    }
    const fontSizeMm = engraving.fontSizeMm;
    const xMm = engraving.centerMmTop && engraving.centerMmTop.x;
    const yMmTop = engraving.centerMmTop && engraving.centerMmTop.y;
    const rotation = engraving.rotationDegClockwise;
    const lineHeightRatio = engraving.lineHeightRatio;
    if (
      !Number.isFinite(fontSizeMm) || fontSizeMm < 1.4 || fontSizeMm > 5.6 + millimeterFloatTolerance ||
      !Number.isFinite(xMm) || xMm < 0 || xMm > 35 ||
      !Number.isFinite(yMmTop) || yMmTop < 0 || yMmTop > 45 ||
      !Number.isFinite(rotation) || rotation < -180 || rotation > 180 ||
      !Number.isFinite(lineHeightRatio) || lineHeightRatio <= 0 || lineHeightRatio !== 1.14
    ) {
      throw new Error("The normalized engraving geometry is invalid.");
    }
    return {engraving, authoritativeFontStack: `"${fontAsset.cssFamily}", serif`};
  };

  const drawNormalizedPreview = (canvas, data, image, previewDesign) => {
    const ctx = canvas.getContext("2d");
    if (!ctx || !image.naturalWidth || !image.naturalHeight) return false;

    const {engraving, authoritativeFontStack} = normalizedEngravingFrom(previewDesign);
    const width = canvas.width;
    const height = canvas.height;
    const fontSize = engraving.fontSizeMm / 35 * width;
    const stack = authoritativeFontStack;
    const ellipse = engravingEllipse(width, height);

    drawStone(ctx, canvas, image);
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(ellipse.cx, ellipse.cy, ellipse.rx, ellipse.ry, 0, 0, Math.PI * 2);
    ctx.clip();
    ctx.translate(engraving.centerMmTop.x / 35 * width, engraving.centerMmTop.y / 45 * height);
    ctx.rotate((engraving.rotationDegClockwise * Math.PI) / 180);
    ctx.font = `400 ${fontSize}px ${stack}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0, 0, 0, 0.24)";
    ctx.shadowBlur = 7;
    ctx.shadowOffsetY = 2;

    const lineHeight = fontSize * engraving.lineHeightRatio;
    engraving.normalizedLines.forEach((line, index) => {
      const offset = (index - (engraving.normalizedLines.length - 1) / 2) * lineHeight;
      ctx.strokeStyle = "rgba(20, 24, 20, 0.2)";
      ctx.lineWidth = Math.max(3, fontSize * 0.08);
      ctx.strokeText(line, 0, offset);
      ctx.fillStyle = colorFrom(engraving.colorHex);
      ctx.fillText(line, 0, offset);
    });
    ctx.restore();
    return true;
  };

  const loadImage = (source) => new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.addEventListener("load", () => resolve(image), {once: true});
    image.addEventListener("error", reject, {once: true});
    image.src = source;
  });

  const loadAuthoritativeFont = async (stack) => {
    if (!document.fonts || typeof document.fonts.load !== "function") {
      throw new Error("The authoritative font loader is unavailable.");
    }
    const descriptor = `400 24px ${stack}`;
    const loadedFaces = await document.fonts.load(descriptor);
    if (!loadedFaces || typeof loadedFaces.length !== "number" || loadedFaces.length < 1) {
      throw new Error("The authoritative font did not load.");
    }
    if (typeof document.fonts.check === "function" && !document.fonts.check(descriptor)) {
      throw new Error("The authoritative font is not ready.");
    }
  };

  const loadFallbackFont = async (stack) => {
    if (!document.fonts || typeof document.fonts.load !== "function") return;
    await document.fonts.load(`400 24px ${stack}`);
  };

  const fetchPreviewDesign = async (designId, fetchRequest) => {
    const response = await fetchRequest(`/apps/kova-designs/designs/${encodeURIComponent(designId)}`, {
      headers: {Accept: "application/json"}
    });
    if (!response.ok) throw new Error("The normalized design is temporarily unavailable.");
    const body = await response.json();
    normalizedEngravingFrom(body && body.previewDesign);
    return body.previewDesign;
  };

  const initCartPreview = async (canvas, dependencies = {}) => {
    const dataScript = canvas.parentElement && canvas.parentElement.querySelector("[data-kova-cart-preview-data]");
    if (!dataScript) return;

    let data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (error) {
      return;
    }
    if (!data.image || (!data.text && !data.designId)) return;

    const request = dependencies.fetch || fetch;
    const imageLoader = dependencies.loadImage || loadImage;
    const authoritativeFontLoader = dependencies.loadFont || loadAuthoritativeFont;
    const fallbackFontLoader = dependencies.loadFallbackFont || loadFallbackFont;
    const normalizedRenderer = dependencies.drawNormalizedPreview || drawNormalizedPreview;
    const readableRenderer = dependencies.drawReadablePreview || drawReadablePreview;
    const wrapper = canvas.parentElement;
    if (wrapper) wrapper.dataset.previewAuthoritative = "false";
    const image = await imageLoader(data.image);

    if (data.designId) {
      try {
        const previewDesign = await fetchPreviewDesign(data.designId, request);
        const {authoritativeFontStack} = normalizedEngravingFrom(previewDesign);
        await authoritativeFontLoader(authoritativeFontStack);
        if (!normalizedRenderer(canvas, data, image, previewDesign)) {
          throw new Error("The normalized preview could not be rendered.");
        }
        if (wrapper) wrapper.dataset.previewAuthoritative = "true";
        canvas.classList.add("is-ready");
        return;
      } catch (error) {
        // Readable line-item properties remain the resilient cart fallback.
      }
    }

    try {
      await fallbackFontLoader(fontStack(data.font));
    } catch (error) {
      // Readable text still provides a useful preview through the fallback stack.
    }
    if (data.text && readableRenderer(canvas, data, image)) {
      canvas.classList.add("is-ready");
    } else {
      canvas.classList.remove("is-ready");
    }
  };

  globalThis.KovaCartPreview = Object.freeze({
    drawNormalizedPreview,
    drawReadablePreview,
    initCartPreview,
  });

  const initCartPreviews = () => {
    document.querySelectorAll("[data-kova-cart-preview]").forEach((canvas) => {
      initCartPreview(canvas).catch(() => canvas.classList.remove("is-ready"));
    });
  };

  document.addEventListener("DOMContentLoaded", initCartPreviews);
})();
